/**
 * Utility functions for date handling. All accept an optional timeZone (e.g. "America/New_York").
 * When timeZone is omitted, UTC is used.
 */

const DEFAULT_TZ = "UTC";

/** Format: "Jan 15" for call history / manager account tables. */
export function formatShortCallDate(
  isoDate: string | null | undefined,
  timeZone: string = DEFAULT_TZ,
): string {
  if (!isoDate) return "--";
  const dt = new Date(isoDate);
  if (Number.isNaN(dt.getTime())) return "--";
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone });
}

/** Appends `start_date` / `end_date` from DateRangeSelector (`yyyy-MM-dd`). */
export function appendDateRange(
  params: URLSearchParams,
  startDate?: string,
  endDate?: string,
): void {
  if (startDate) params.set("start_date", startDate);
  if (endDate) params.set("end_date", endDate);
}

/** True when `endDateTime` is in the past (used for completed meeting badges). */
export function isMeetingEnded(endDateTime?: string): boolean {
  if (!endDateTime) return false;
  try {
    const end = new Date(endDateTime);
    if (Number.isNaN(end.getTime())) return false;
    return end.getTime() < Date.now();
  } catch {
    return false;
  }
}

function getParts(date: Date, timeZone: string) {
  const dayName = date.toLocaleDateString("en-US", { weekday: "short", timeZone });
  const month = date.toLocaleDateString("en-US", { month: "short", timeZone });
  const day = date.toLocaleDateString("en-US", { day: "numeric", timeZone });
  const year = date.toLocaleDateString("en-US", { year: "numeric", timeZone });
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  });
  return { dayName, month, day, year, time };
}

/**
 * Format: "Mon Jan 15 @ 2:30 PM"
 */
export function formatMeetingDate(dateString: string, timeZone: string = DEFAULT_TZ): string {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const { dayName, month, day, time } = getParts(date, timeZone);
    return `${dayName} ${month} ${day} @ ${time}`;
  } catch {
    return dateString;
  }
}

/**
 * Format: "Jan 15, 2025 at 2:30 PM"
 */
export function formatMeetingDateLong(dateString: string, timeZone: string = DEFAULT_TZ): string {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const { month, day, year, time } = getParts(date, timeZone);
    return `${month} ${day}, ${year} at ${time}`;
  } catch {
    return dateString;
  }
}

/**
 * Format: "Jan 15, 2025, 2:30 PM" (date + time)
 */
export function formatDateTime(dateString: string, timeZone: string = DEFAULT_TZ): string {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const month = date.toLocaleDateString("en-US", { month: "short", timeZone });
    const day = date.toLocaleDateString("en-US", { day: "numeric", timeZone });
    const year = date.toLocaleDateString("en-US", { year: "numeric", timeZone });
    const time = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone,
    });
    return `${month} ${day}, ${year}, ${time}`;
  } catch {
    return dateString;
  }
}

/**
 * Format: "Jan 15, 2025" (date only)
 */
export function formatDateOnly(dateString: string, timeZone: string = DEFAULT_TZ): string {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone,
    });
  } catch {
    return dateString;
  }
}
